import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { ProjectStatus, SuperProject } from "../types";
import { formatDate, formatText } from "../../../utils/utils";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";
import { PROJECT_TERMINOLOGY } from "../enums";
import { UserScope } from "../../../types/operationType";

export interface SuperProjectListProps {
  superProjects: SuperProject[];
  onView: (superProject: SuperProject) => void;
  onEdit: (superProject: SuperProject) => void;
  onDelete: (id: string) => void;
  userScope: UserScope;
}

const SuperProjectList: React.FC<SuperProjectListProps> = ({
  superProjects,
  onView,
  onEdit,
  onDelete,
  userScope,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<SuperProject | null>(
    null
  );

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    superProject: SuperProject
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(superProject);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleEdit = () => {
    if (selectedProject) {
      onEdit(selectedProject);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedProject) {
      onDelete(selectedProject.id);
    }
    handleMenuClose();
  };

  if (superProjects.length === 0) {
    return (
      <Paper sx={{ mt: 2, p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No {PROJECT_TERMINOLOGY.LOWER_PLURAL} found
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mt: 2 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyles}>
                {PROJECT_TERMINOLOGY.SINGULAR} Name
              </TableCell>
              <TableCell sx={tableHeaderStyles}>Status</TableCell>
              <TableCell sx={tableHeaderStyles}>Client</TableCell>
              <TableCell sx={tableHeaderStyles}>Divisions</TableCell>
              <TableCell sx={tableHeaderStyles}>Start Date</TableCell>
              <TableCell sx={tableHeaderStyles}>End Date</TableCell>
              <TableCell sx={tableHeaderStyles}>Created By</TableCell>
              {userScope === UserScope.GLOBAL && (
                <TableCell sx={tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {superProjects?.map((superProject) => {
              const isTerminated =
                superProject.status === ProjectStatus.TERMINATED;
              return (
                <TableRow key={superProject.id} hover>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: "0.8rem",
                        color: isTerminated ? "text.secondary" : "text.primary",
                      }}
                      onClick={() => onView(superProject)}
                    >
                      {superProject.name}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {formatText(superProject.status)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {superProject.client?.clientName}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {superProject.divisions &&
                    superProject.divisions?.length > 0
                      ? superProject.divisions?.length
                      : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {formatDate(superProject.startDate)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {superProject.endDate
                      ? formatDate(superProject.endDate)
                      : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {superProject.createdByUser?.name}
                  </TableCell>
                  {userScope === UserScope.GLOBAL && (
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        color: isTerminated ? "text.secondary" : "text.primary",
                      }}
                      align="center"
                    >
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, superProject)}
                        >
                          <MoreVertIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default SuperProjectList;
